<template>
  <q-menu
    v-if="selected"
    anchor="top left"
    self="bottom start"
    class="image-menu"
    persistent
    :offset="[0,5]"
    :target="$refs.imageWrapper"
    no-focus
  >
    <q-btn
      size="17px"
      flat
      icon="photo_size_select_small"
      @click.stop="onShowMobileMenu"
    >
      <q-tooltip
        anchor="top middle"
      >
        {{ $t('editor.image.changeWidth') }}
      </q-tooltip>
      <q-menu
        v-if="!isMobileView"
      >
        <q-item>
          <q-input
            ref="widthInput"
            v-model.number="newWidth"
            dense
            outlined
            type="number"
            suffix="px"
            :label="$t('editor.image.width')"
            :rules="widthRules"
            @focusout="clampWidth"
            @keydown.enter="isValidWidth ? onChangeImageWidth : null"
          >
            <template #after>
              <q-btn
                round
                dense
                flat
                :disable="!isValidWidth"
                :class="{'text-grey-3': !isValidWidth}"
                icon="done"
                @click="onChangeImageWidth"
              />
              <q-tooltip v-if="!isValidWidth">
                {{ $t('editor.image.imageIsSmall') }}
              </q-tooltip>
            </template>
          </q-input>
        </q-item>
      </q-menu>
    </q-btn>
    <q-btn
      size="17px"
      flat
      icon="delete"
      data-delete-img
      @click.stop="onDeleteImage"
    >
      <q-tooltip
        anchor="top middle"
      >
        {{ $t('editor.image.deleteImage') }}
      </q-tooltip>
    </q-btn>
  </q-menu>
</template>

<script>
import { nodeViewProps } from '@tiptap/vue-3';
import EditorResizeImageMobileDialog from 'components/Editor/EditorResizeImageMobileDialog';
import { clamp } from 'lodash';
import Interactions from 'src/mixins/Interactions';

export default {
  name: 'EditorResizeImageMenu',

  mixins: [Interactions],

  inject: ['isImageDeleted'],

  props: {
    selected: nodeViewProps.selected,
    deleteNode: nodeViewProps.deleteNode,

    width: {
      type: Number,
      required: true,
    },

    minWidth: {
      type: Number,
      required: true,
    },

    maxWidth: {
      type: Number,
      required: true,
    },
  },

  emits: ['show-width-menu', 'change-width'],

  data() {
    return {
      newWidth: 0,
    };
  },
  computed: {
    isMobileView() {
      return this.$q.platform.is.mobile || this.$q.screen.lt.sm;
    }, // isMobileView()

    isValidWidth() {
      return this.newWidth >= this.minWidth && this.newWidth <= this.maxWidth;
    }, // isValidWidth()

    widthRules() {
      return [
        (val) => val <= this.maxWidth || this.$t('editor.image.imageIsSmall'),
        (val) => val >= this.minWidth || this.$t('editor.image.imageIsSmall'),
      ];
    }, // widthRules()
  },

  watch: {
    width: {
      immediate: true,
      handler(width) {
        this.newWidth = width;
      },
    },
  },

  methods: {

    clampWidth() {
      this.newWidth = clamp(this.newWidth, this.minWidth, this.maxWidth);
    },

    onShowMobileMenu() {
      if (!this.isMobileView) {
        this.$emit('show-width-menu');
        return;
      }

      this.openComponentDialog(
        EditorResizeImageMobileDialog,
        {
          width: this.width,
          minWidth: this.minWidth,
          maxWidth: this.maxWidth,
          rules: this.widthRules,
        },
        (newWidth) => {
          this.newWidth = newWidth;
          this.onChangeImageWidth();
        },
      );
    }, // onShowMobileMenu()

    onChangeImageWidth() {
      this.$emit('change-width', this.newWidth);
    },

    onDeleteImage() {
      this.deleteNode();

      // changing injected obj property to be able to see delete button click from the TextEditor component
      // and emit 'image-deleted' event from there
      if ('value' in this.isImageDeleted) {
        this.isImageDeleted.value = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.image-menu {
  box-shadow: $shadow-1;
  background-color: #fff;
}
</style>
